import React, { useState, useEffect, useContext } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { AuthContext } from '../../AuthContext';
import UseToast from '../UseToast';
import { FaEye, FaEyeSlash } from 'react-icons/fa';

export default function AddClientForm() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { showMsgSuccessToast, showErrorToast } = UseToast();
  const { authState } = useContext(AuthContext);
  const [showPin, setShowPin] = useState(false);

  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    username: '',
    email: '',
    password: '',
    frontend_url: '',
    user_selected_system: '',
    bypass_menu_login: '',
    company_name: '',
    client_pin: '',
  });

  const [loading, setLoading] = useState(false);

  const handleInputChange = e => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value,
    });
  };

  const handleSubmit = async e => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await fetch('/auth/register', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      const data = await response.json();

      if (response.status === 201) {
        showMsgSuccessToast(data.message);
        navigate(`/availablity-settings/${data?.user?._id}`);
      } else {
        throw new Error(data.message || 'An error occurred');
      }
    } catch (error) {
      console.error('Error:', error);
      showErrorToast('An error occurred: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmitUpdate = async e => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await fetch(`/auth/users/${id}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      const data = await response.json();

      if (response.ok) {
        showMsgSuccessToast(data.message);
      } else {
        showErrorToast(data.message);
      }
    } catch (error) {
      console.error('Error:', error);
      showErrorToast('An error occurred while updating');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (id) {
      setLoading(true);
      const endpoint = authState?.user?.role_id === 1 ? '/auth/users' : `/auth/userprofile/${id}`;

      fetch(endpoint)
        .then(response => response.json())
        .then(resp => {
          const userData =
            authState?.user?.role_id === 1
              ? resp?.users?.find(client => client._id === id)
              : resp?.user;

          if (userData) {
            setFormData({
              ...userData,
              password: '',
            });
          }
        })
        .catch(error => {
          console.error('Error fetching client data:', error);
          showErrorToast('Failed to load client data');
        })
        .finally(() => setLoading(false));
    }
  }, [id, authState?.user?.role_id]);

  const isEditMode = !!id;
  const isProfileView = authState?.user?.role_id !== 1;

  return (
    <div className="main-container">
      <div className="pd-ltr-20 xs-pd-20-10">
        <div className="min-height-200px">
          {/* Modern Header */}
          <div className="page-header border-bottom mb-4">
            <div className="row align-items-center">
              <div className="col-md-6 col-sm-12">
                <div className="title">
                  <h4 className="text-primary fw-700">
                    {isEditMode
                      ? isProfileView
                        ? 'Edit Profile'
                        : 'Edit Client'
                      : 'Add New Client'}
                  </h4>
                </div>
                <nav aria-label="breadcrumb" role="navigation">
                  <ol className="breadcrumb">
                    <li className="breadcrumb-item">
                      <a href="/">Home</a>
                    </li>
                    <li className="breadcrumb-item active" aria-current="page">
                      {isEditMode ? 'Edit' : 'Add Client'}
                    </li>
                  </ol>
                </nav>
              </div>

              <div className="col-md-6 col-sm-12 text-end">
                <button
                  className="btn btn-outline-secondary btn-back-effect"
                  style={{
                    '--brand-color': authState?.user?.brandColor || '#000', // fallback if no brandColor
                  }}
                  onClick={() => navigate(-1)}
                >
                  <i className="bi bi-arrow-left me-2"></i>
                  Back
                </button>
              </div>
            </div>
          </div>

          <div className="row justify-content-center">
            <div className="col-xl-10 col-lg-10 col-md-12 col-sm-12 mb-30">
              {/* Modern Card Design */}
              <div
                className="card-box border-radius-10 overflow-hidden"
                style={{ boxShadow: 'rgba(0, 0, 0, 0.24) 0px 3px 8px' }}
              >
                <div className="card-header bg-light border-bottom-0 py-4">
                  <h5 className="card-title text-dark mb-0 fw-600">
                    <i className="bi bi-person-plus-fill me-2 text-primary"></i>
                    {isEditMode
                      ? isProfileView
                        ? 'Profile Information'
                        : 'Client Information'
                      : 'Create New Client Account'}
                  </h5>
                  <p className="text-muted mb-0 mt-1">
                    {isEditMode
                      ? 'Update the information below'
                      : 'Fill in the details to create a new client account'}
                  </p>
                </div>

                <div className="card-body p-4">
                  {loading ? (
                    <div className="text-center py-5">
                      <div className="spinner-border text-primary" role="status">
                        <span className="visually-hidden">Loading...</span>
                      </div>
                      <p className="mt-2 text-muted">Loading client data...</p>
                    </div>
                  ) : (
                    <form onSubmit={isEditMode ? handleSubmitUpdate : handleSubmit}>
                      {/* Company & Username Row */}
                      <div className="row mb-4">
                        <div className="col-md-6">
                          <div className="form-group">
                            <label className="form-label fw-600 text-dark">
                              <i className="bi bi-building me-2 text-primary"></i>
                              Company Name
                            </label>
                            <input
                              type="text"
                              className="form-control form-control-lg border-radius-8"
                              name="company_name"
                              value={formData.company_name || ''}
                              onChange={handleInputChange}
                              placeholder="Enter company name"
                              required
                            />
                          </div>
                        </div>
                        <div className="col-md-6">
                          <div className="form-group">
                            <label className="form-label fw-600 text-dark">
                              <i className="bi bi-person me-2 text-primary"></i>
                              Username
                            </label>
                            <input
                              className="form-control form-control-lg border-radius-8"
                              type="text"
                              name="username"
                              value={formData.username || ''}
                              onChange={handleInputChange}
                              placeholder="Choose a username"
                              required
                            />
                          </div>
                        </div>
                      </div>

                      {/* Email & Password Row */}
                      <div className="row mb-4">
                        <div className="col-md-6">
                          <div className="form-group">
                            <label className="form-label fw-600 text-dark">
                              <i className="bi bi-envelope me-2 text-primary"></i>
                              Email Address
                            </label>
                            <input
                              className="form-control form-control-lg border-radius-8"
                              type="email"
                              name="email"
                              value={formData.email || ''}
                              onChange={handleInputChange}
                              placeholder="client@company.com"
                              required
                            />
                          </div>
                        </div>
                        <div className="col-md-6">
                          <div className="form-group">
                            <label className="form-label fw-600 text-dark">
                              <i className="bi bi-lock me-2 text-primary"></i>
                              Password
                            </label>
                            <input
                              className="form-control form-control-lg border-radius-8"
                              type="password"
                              name="password"
                              value={formData.password || ''}
                              onChange={handleInputChange}
                              placeholder={
                                isEditMode ? 'Leave blank to keep current' : 'Enter password'
                              }
                              required={!isEditMode}
                            />
                            {isEditMode && (
                              <small className="form-text text-muted">
                                Leave blank to keep current password
                              </small>
                            )}
                          </div>
                        </div>
                      </div>

                      {/* Client Pin */}
                      <div className="row mb-4">
                        <div className="col-md-6">
                          <div className="form-group">
                            <label className="form-label fw-600 text-dark">
                              <i className="bi bi-shield-lock me-2 text-primary"></i>
                              Client PIN
                            </label>
                            <div className="position-relative">
                              <input
                                className="form-control form-control-lg border-radius-8 pe-5"
                                type={showPin ? 'text' : 'password'}
                                inputMode="numeric"
                                maxLength="4"
                                name="client_pin"
                                value={formData.client_pin || ''}
                                onChange={e => {
                                  const value = e.target.value.replace(/\D/g, '').slice(0, 4);
                                  handleInputChange({
                                    target: { name: 'client_pin', value },
                                  });
                                }}
                                placeholder="Enter 4-digit PIN"
                              />
                              <span
                                className="toggle-visibility-icon"
                                onClick={() => setShowPin(!showPin)}
                              >
                                {!showPin ? <FaEyeSlash /> : <FaEye />}
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Submit Button */}
                      <div className="row mt-5">
                        <div className="col-12">
                          <div className="d-flex gap-3 justify-content-end">
                            <button
                              type="button"
                              className="btn btn-outline-secondary btn-lg px-4"
                              onClick={() => navigate(-1)}
                            >
                              Cancel
                            </button>
                            <button
                              type="submit"
                              className="btn btn-primary btn-lg px-4"
                              disabled={loading}
                            >
                              {loading ? (
                                <>
                                  <span
                                    className="spinner-border spinner-border-sm me-2"
                                    role="status"
                                  ></span>
                                  {isEditMode ? 'Updating...' : 'Creating...'}
                                </>
                              ) : (
                                <>
                                  <i
                                    className={`bi ${
                                      isEditMode ? 'bi-check-circle' : 'bi-person-plus'
                                    } me-2`}
                                  ></i>
                                  {isEditMode ? 'Update' : 'Create Client'}
                                </>
                              )}
                            </button>
                          </div>
                        </div>
                      </div>
                    </form>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Add some custom styles */}
      <style jsx>{`
        .border-radius-8 {
          border-radius: 8px;
        }
        .border-radius-10 {
          border-radius: 10px;
        }
        .card-header {
          background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
        }
        .form-control:focus {
          border-color: #4e73df;
          box-shadow: 0 0 0 0.2rem rgba(78, 115, 223, 0.25);
        }
        .security-section {
          border-left: 4px solid #28a745 !important;
        }
        .btn-back-effect:hover {
          background-color: var(--brand-color) !important;
          color: #fff !important;
          transition: background-color 0.2s ease-in-out;
        }
        .position-relative {
          position: relative;
        }

        .toggle-visibility-icon {
          position: absolute;
          top: 50%;
          right: 12px;
          transform: translateY(-50%);
          cursor: pointer;
          font-size: 1.2rem;
          color: #6c757d;
          transition: color 0.2s ease-in-out;
        }

        .toggle-visibility-icon:hover {
          color: var(--brand-color, #0d6efd);
        }
      `}</style>
    </div>
  );
}
